<template>
	<SplashCursor v-if="!isMobile()" :SIM_RESOLUTION="256" :DYE_RESOLUTION="140" :CAPTURE_RESOLUTION="512"
		:DENSITY_DISSIPATION="4.5" :VELOCITY_DISSIPATION="2" :PRESSURE="0.1" :PRESSURE_ITERATIONS="20" :CURL="3"
		:SPLAT_RADIUS="0.2" :SPLAT_FORCE="9000" :SHADING="true" :COLOR_UPDATE_SPEED="10"
		:BACK_COLOR="{ r: 0.5, g: 0, b: 0 }" :TRANSPARENT="true" :HUE_MIN="0.4" :HUE_MAX="0.7" :COLOR_INTENSITY="0.22"
		:MIN_SPLAT_SPEED="0.015" :DISABLE_CLICK_SPLAT="true" />
	<div
		class="min-h-screen relative overflow-x-hidden bg-gradient-to-b from-sky-50 via-sky-100 to-white dark:from-[#0b1220] dark:via-[#0d1b2a] dark:to-[#0b1220] blue-theme">
		<!-- 背景动画（置于最底层） -->
		<div ref="vantaRef" class="vanta-bg"></div>

		<!-- 顶部栏：占据全宽，简洁，含侧边栏开关（移动端用抽屉） -->
		<header
			class="fixed top-0 left-0 right-0 h-14 bg-white/70 backdrop-blur border-b border-sky-100/70 z-20 shadow-[0_2px_12px_rgba(14,165,233,0.15)]">
			<div class="w-full h-full px-4 flex items-center justify-between"
				style="padding-left: max(1rem, env(safe-area-inset-left)); padding-right: max(1rem, env(safe-area-inset-right));">
				<div class="font-bold text-base md:text-lg">
					<img src="/assets/uestc.webp" alt="logo" class="inline-block h-6 mr-2 -mt-1" />
					金融学基础 - 通用计算器
				</div>
				<div class="flex items-center gap-3">
					<CurrencySwitcher />
					<el-button link class="hidden md:inline-flex" @click="collapsed = !collapsed" title="展开/收起侧边栏">
						<el-icon>
							<Menu />
						</el-icon>
					</el-button>
					<el-button link class="md:hidden inline-flex" @click="drawerVisible = true" title="打开菜单">
						<el-icon>
							<Menu />
						</el-icon>
					</el-button>
				</div>
			</div>
		</header>

		<!-- 主体布局：左侧边栏 + 中心内容区域（固定最大宽度） -->
		<div class="pt-14"> <!-- 预留头部高度 -->
			<div class="flex">
				<!-- 桌面侧边栏（固定定位） -->
				<aside
					class="hidden md:block fixed top-14 left-0 bottom-0 z-10 transition-all duration-300 shadow-[0_2px_12px_rgba(0,0,0,0.08)]"
					:style="{ width: collapsed ? '64px' : '220px' }">
					<div class="h-full p-2">
						<div class="glass-aside h-full rounded-xl overflow-hidden flex flex-col">
							<!-- 折叠条（参考示例） -->
							<div class="h-10 flex items-center border-b border-gray-100 px-3 select-none">
								<el-icon class="cursor-pointer"
									:style="{ transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }"
									@click="collapsed = !collapsed">
									<component :is="collapsed ? Expand : Fold" />
								</el-icon>
								<transition name="fade-slide">
									<span v-show="!collapsed" class="ml-3 text-sm text-gray-600">导航</span>
								</transition>
							</div>
							<!-- 菜单（内部滚动容器） -->
							<div class="flex-1 min-h-0 overflow-auto">

								<el-menu class="!border-r-0" :default-active="active" @select="onSelect"
									:collapse="collapsed">
									<el-sub-menu index="loan">
										<template #title>
											<span class="ml-2" v-show="!collapsed">贷款计算</span>
										</template>
										<el-menu-item index="/calc/standard"><span>标准计算</span></el-menu-item>
										<el-menu-item index="/calc/reverse"><span>反向计算</span></el-menu-item>
										<el-menu-item index="/calc/prepay"><span>提前还款</span></el-menu-item>
										<el-sub-menu index="scenario">
											<template #title>
												<span class="ml-2" v-show="!collapsed">具体情境计算</span>
											</template>
											<el-menu-item index="/calc/tvm">年金PV/FV/PMT/n/r</el-menu-item>
											<el-menu-item index="/calc/irr">实际利率(IRR)</el-menu-item>
											<el-menu-item index="/calc/retirement">退休规划</el-menu-item>
											<el-menu-item index="/calc/leasing">分期付款</el-menu-item>
										</el-sub-menu>
									</el-sub-menu>
									<el-sub-menu index="invest">
										<template #title>
											<span class="ml-2" v-show="!collapsed">投资分析</span>
										</template>
										<el-menu-item index="/invest/financing">融资计划</el-menu-item>
										<el-menu-item index="/invest/bep">盈亏平衡</el-menu-item>
										<!-- future items: 折旧/NPV 模块、敏感性分析等 -->
									</el-sub-menu>
								</el-menu>
							</div>
						</div>
					</div>
				</aside>

				<!-- 占位元素：用于为固定侧边栏预留空间（仅桌面端显示） -->
				<div class="hidden md:block" :style="{ width: collapsed ? '64px' : '220px' }"></div>

				<!-- 移动端抽屉侧边栏：全屏宽度 -->
				<el-drawer v-model="drawerVisible" direction="ltr" :with-header="false" class="md:hidden"
					:size="'100%'">
					<div class="p-2">
						<el-menu :default-active="active" @select="(i) => { onSelect(i); drawerVisible = false }">
							<el-sub-menu index="loan-m">
								<template #title>
									<el-icon>
										<Collection />
									</el-icon>
									<span class="ml-2">贷款计算</span>
								</template>
								<el-menu-item index="/calc/standard">标准计算</el-menu-item>
								<el-menu-item index="/calc/reverse">反向计算</el-menu-item>
								<el-menu-item index="/calc/prepay">提前还款</el-menu-item>
								<el-sub-menu index="scenario-m">
									<template #title>
										<el-icon></el-icon>
										<span class="ml-2">具体情境计算</span>
									</template>
									<el-menu-item index="/calc/tvm">年金PV/FV/PMT/n/r</el-menu-item>
									<el-menu-item index="/calc/irr">实际利率(IRR)</el-menu-item>
									<el-menu-item index="/calc/retirement">退休规划</el-menu-item>
									<el-menu-item index="/calc/leasing">分期付款</el-menu-item>
								</el-sub-menu>
							</el-sub-menu>
							<el-sub-menu index="invest-m">
								<template #title>
									<el-icon>
										<Collection />
									</el-icon>
									<span class="ml-2">投资分析</span>
								</template>
								<el-menu-item index="/invest/financing">融资计划</el-menu-item>
								<el-menu-item index="/invest/bep">盈亏平衡</el-menu-item>
							</el-sub-menu>
						</el-menu>
					</div>
				</el-drawer>

				<!-- 中心内容区域（固定最大宽度） -->
				<main class="flex-1 px-3 md:px-6">
					<div class="max-w-5xl mx-auto py-4 md:py-8 pb-20"
						style="padding-left: max(0.75rem, env(safe-area-inset-left)); padding-right: max(0.75rem, env(safe-area-inset-right));">
						<div class="glass-card rounded-2xl p-4 md:p-6">
							<router-view v-slot="{ Component }">
								<transition name="fade-slide" mode="out-in">
									<div class="route-shell" :key="active">
										<component :is="Component" />
									</div>
								</transition>
							</router-view>
						</div>
					</div>
				</main>

				<!-- 固定页脚版权信息（全宽） -->
				<footer class="fixed bottom-0 left-0 right-0 z-10">
					<div class="max-w-5xl mx-auto px-3 md:px-6 py-2"
						style="padding-left: max(0.75rem, env(safe-area-inset-left)); padding-right: max(0.75rem, env(safe-area-inset-right)); padding-bottom: env(safe-area-inset-bottom);">
						<p
							class="text-center text-[11px] md:text-xs text-gray-500 opacity-70 hover:text-sky-700 hover:opacity-100 transition-colors duration-200">
							&copy; {{ currentYear }} 电子科技大学 | 唐孝玉 蔡礼斌
						</p>
					</div>
				</footer>
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Menu } from '@element-plus/icons-vue'
import { Collection } from '@element-plus/icons-vue'
import SplashCursor from '@/SplashCursor/SplashCursor.vue'
import CurrencySwitcher from '@/components/ui/CurrencySwitcher.vue'
// 移动端检测
function isMobile() {
	return /Android|webOS|iPhone|iPod|iPad|BlackBerry|Mobile/i.test(navigator.userAgent)
}
// 用于侧边栏折叠图标
import { Expand, Fold } from '@element-plus/icons-vue'
// 顶部菜单激活态
const route = useRoute()
const router = useRouter()
const active = ref(route.path)
watch(() => route.path, (p) => { active.value = p })

const onSelect = (index) => { router.push(index) }
const collapsed = ref(false)
const drawerVisible = ref(false)

// 年份
const currentYear = new Date().getFullYear()

</script>

<style scoped>
/* 主题变量（仅作用于本页） */
.blue-theme {
	/* Element Plus 主色调（sky-500） */
	--el-color-primary: #0ea5e9;
	--el-color-primary-light-3: #38bdf8;
	--el-color-primary-light-5: #7dd3fc;
	--el-color-primary-light-7: #bae6fd;
	--el-color-primary-light-8: #e0f2fe;
	--el-color-primary-light-9: #f0f9ff;
	--el-color-primary-dark-2: #0284c7;

	/* 菜单色板：透明背景 + 蓝色悬停/激活 */
	--el-menu-bg-color: transparent;
	--el-menu-hover-bg-color: rgba(14, 165, 233, 0.08);
	--el-menu-active-color: #0284c7;
	/* sky-600 */
	--el-text-color-regular: #0f172a;
	/* slate-900 */
}

/* 移动端：菜单区域排版优化 */
.el-dropdown-link {
	cursor: pointer;
	user-select: none;
}

/* 背景层 */
.vanta-bg {
	position: fixed;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100vh;
	z-index: -1;
	/* 确保永远在内容下方 */
	pointer-events: none;
}

/* 侧边栏与卡片的磨砂玻璃风格 */
.glass-card {
	/* 轻微蓝色调的磨砂玻璃 */
	background: linear-gradient(180deg, rgba(255, 255, 255, 0.70) 0%, rgba(236, 248, 255, 0.60) 100%);
	backdrop-filter: blur(12px);
	-webkit-backdrop-filter: blur(12px);
	border: 1px solid rgba(14, 165, 233, 0.20);
	/* sky-500 */
	box-shadow: 0 10px 30px rgba(14, 165, 233, 0.12);
}

.glass-aside {
	background: rgba(255, 255, 255, 0.9);
	backdrop-filter: blur(10px);
	-webkit-backdrop-filter: blur(10px);
	border: 1px solid rgba(255, 255, 255, 0.65);
}

/* 移动端：约束路由内容宽度，防止公式撑宽，允许横向滚动查看公式 */
.route-shell {
	max-width: 100%;
}

@media (max-width: 768px) {
	.route-shell {
		overflow-x: auto;
		-webkit-overflow-scrolling: touch;
	}

	/* KaTeX/公式容器的常见类名，尽量不侵入具体组件 */
	.route-shell .katex-display,
	.route-shell .katex {
		max-width: 100%;
	}
}

/* 路由切换与展开动画 */
.fade-slide-enter-active,
.fade-slide-leave-active {
	transition: all .25s ease;
}

.fade-slide-enter-from {
	opacity: 0;
	transform: translateY(6px);
}

.fade-slide-leave-to {
	opacity: 0;
	transform: translateY(-6px);
}
</style>
